import { supabase } from './client';
import type { JobSeekerProfile, JobSeekerProfileData } from '../../types/profiles';

export async function uploadJobSeekerResume(file: File, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `job-seekers/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-applications')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-applications')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadJobSeekerAudio(file: File, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/audio-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-seeker-audio')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-seeker-audio')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadJobSeekerPhoto(file: File, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-photo-${Date.now()}.${fileExt}`;
    const filePath = `job-seekers/photos/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-applications')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-applications')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadJobSeekerVoiceRecording(audioBlob: Blob, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const uuid = crypto.randomUUID();
    const filePath = `${userId}/${uuid}.webm`;

    const { data, error } = await supabase.storage
      .from('job-seeker-audio')
      .upload(filePath, audioBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'audio/webm'
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-seeker-audio')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

export async function uploadJobSeekerVideo(file: File, userId: string): Promise<{ url: string | null; error: Error | null }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-video-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('job-seeker-videos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('job-seeker-videos')
      .getPublicUrl(filePath);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    return { url: null, error: error as Error };
  }
}

function validateProfileData(data: JobSeekerProfileData): boolean {
  return (
    Array.isArray(data.jobTypes) && data.jobTypes.length > 0 &&
    Array.isArray(data.locations) && data.locations.length > 0 &&
    Array.isArray(data.skills) && data.skills.length > 0 &&
    typeof data.experience === 'string' && data.experience.length > 0 &&
    typeof data.availability === 'string' && data.availability.length > 0 &&
    typeof data.desiredPay === 'string' &&
    Array.isArray(data.certifications) &&
    typeof data.about === 'string'
  );
}

export async function addJobSeekerProfile(
  userId: string,
  profileData: JobSeekerProfileData
) {
  
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!validateProfileData(profileData)) {
      throw new Error('Invalid profile data');
    }

    // Clean empty strings from arrays
    const cleanedData = {
      ...profileData,
      locations: profileData.locations.filter(Boolean),
      skills: profileData.skills.filter(Boolean),
      certifications: profileData.certifications.filter(Boolean)
    };

    let resumeUrl: string | null = null;
    let audioUrl: string | null = null;
    let photoUrl: string | null = cleanedData.photoUrl || null;
    let videoUrl: string | null = null;

    if (profileData.resumeFile) {
      const { url, error: uploadError } = await uploadJobSeekerResume(profileData.resumeFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload resume: ${uploadError.message}`);
      }
      resumeUrl = url;
    }

    if (profileData.audioFile) {
      const { url, error: uploadError } = await uploadJobSeekerAudio(profileData.audioFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload audio: ${uploadError.message}`);
      }
      audioUrl = url;
    }

    if (profileData.photoFile) {
      const { url, error: uploadError } = await uploadJobSeekerPhoto(profileData.photoFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload photo: ${uploadError.message}`);
      }
      photoUrl = url;
    }

    if (profileData.videoFile) {
      const { url, error: uploadError } = await uploadJobSeekerVideo(profileData.videoFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload video: ${uploadError.message}`);
      }
      videoUrl = url;
    }
    const { data, error } = await supabase
      .from('job_seekers')
      .insert({
        job_types: cleanedData.jobTypes,
        locations: cleanedData.locations,
        skills: cleanedData.skills,
        experience: cleanedData.experience,
        availability: cleanedData.availability,
        desired_pay: cleanedData.desiredPay,
        certifications: cleanedData.certifications,
        about: cleanedData.about,
        photo_url: photoUrl,
        email: cleanedData.email,
        phone: cleanedData.phone,
        resume_url: resumeUrl,
        audio_url: audioUrl,
        voice_intro_url: cleanedData.voiceIntroUrl,
        video_url: videoUrl,
        user_id: userId,
      })
      .select()
      .single();
    
    if (error) {
      console.error('Supabase error creating profile:', error);
      throw error;
    }

    if (!data || !data.id) {
      throw new Error('Profile creation failed - no data returned');
    }

    return { id: data.id, error: null };
  } catch (error) {
    return { id: null, error: error as Error };
  }
}

export async function updateJobSeekerProfile(
  profileId: string,
  userId: string,
  profileData: Partial<JobSeekerProfileData>
) {
  try {
    // Clean empty strings from arrays
    const cleanedData = {
      ...profileData,
      locations: profileData.locations?.filter(Boolean),
      skills: profileData.skills?.filter(Boolean),
      certifications: profileData.certifications?.filter(Boolean)
    };

    let resumeUrl: string | undefined = cleanedData.resumeUrl;
    let audioUrl: string | undefined = cleanedData.audioUrl;
    let photoUrl: string | undefined = cleanedData.photoUrl;
    let videoUrl: string | undefined = cleanedData.videoUrl;

    if (profileData.resumeFile) {
      const { url, error: uploadError } = await uploadJobSeekerResume(profileData.resumeFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload resume: ${uploadError.message}`);
      }
      resumeUrl = url || undefined;
    }

    if (profileData.audioFile) {
      const { url, error: uploadError } = await uploadJobSeekerAudio(profileData.audioFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload audio: ${uploadError.message}`);
      }
      audioUrl = url || undefined;
    }

    if (profileData.photoFile) {
      const { url, error: uploadError } = await uploadJobSeekerPhoto(profileData.photoFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload photo: ${uploadError.message}`);
      }
      photoUrl = url || undefined;
    }

    if (profileData.videoFile) {
      const { url, error: uploadError } = await uploadJobSeekerVideo(profileData.videoFile, userId);
      if (uploadError) {
        throw new Error(`Failed to upload video: ${uploadError.message}`);
      }
      videoUrl = url || undefined;
    }

    const { error } = await supabase
      .from('job_seekers')
      .update({
        job_types: cleanedData.jobTypes,
        locations: cleanedData.locations,
        skills: cleanedData.skills,
        experience: cleanedData.experience,
        availability: cleanedData.availability,
        desired_pay: cleanedData.desiredPay,
        certifications: cleanedData.certifications,
        about: cleanedData.about,
        photo_url: photoUrl,
        email: cleanedData.email,
        phone: cleanedData.phone,
        resume_url: resumeUrl,
        audio_url: audioUrl,
        voice_intro_url: cleanedData.voiceIntroUrl,
        video_url: videoUrl,
        updated_at: new Date().toISOString()
      })
      .eq('id', profileId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}

export async function getJobSeekerProfiles() {
  try {
    const { data, error } = await supabase
      .from('job_seekers')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    const profiles = (data || []).map(profile => ({
      id: profile.id,
      userId: profile.user_id,
      jobTypes: profile.job_types || [],
      locations: profile.locations || [],
      skills: profile.skills || [],
      experience: profile.experience || '',
      availability: profile.availability || '',
      desiredPay: profile.desired_pay || '',
      certifications: profile.certifications || [],
      about: profile.about || '',
      photoUrl: profile.photo_url,
      email: profile.email,
      phone: profile.phone,
      resumeUrl: profile.resume_url,
      audioUrl: profile.audio_url,
      voiceIntroUrl: profile.voice_intro_url,
      videoUrl: profile.video_url,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at
    })) as JobSeekerProfile[];
    
    return { profiles, error: null };
  } catch (error) {
    return { profiles: [], error: error as Error };
  }
}

export async function deleteJobSeekerProfile(profileId: string) {
  try {
    const { error } = await supabase
      .from('job_seekers')
      .delete()
      .eq('id', profileId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error: error as Error };
  }
}